<template>
  <DynamicTabs
    class="mt-3"
    v-model:active-tab="activeTab"
    v-model:tabs="tabs"
    :show-add="canEdit"
    :show-edit="canEdit"
    :show-delete="canEdit"
    rearrangeable
    @add-tab="addTab"
    @edit-tab="editTab"
    @delete-tab="deleteTab"
    @tabs-rearranged="tabsRearranged"
  ></DynamicTabs>

  <div class="h-[500px]">
    <ag-grid-vue
      theme="legacy"
      :rowData="tabbedWaivers"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      :singleClickEdit="true"
      class="pb-3 h-full"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      :pinnedBottomRowData="[{}]"
      :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
      :fullWidthCellRenderer="CustomAddNewCellRenderer"
      :fullWidthCellRendererParams="{
        callback: () => addNewTabbedWaiver(),
        label: 'waiver',
        display: canEdit,
      }"
      @grid-size-changed="resize"
      @cell-editing-stopped="recalculate"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomDateCellRenderer from "@/components/cell-renderers/CustomDateCellRenderer.vue";
import CustomAddNewCellRenderer from "@/components/cell-renderers/CustomAddNewCellRenderer.vue";
import { usePropsStore } from "@/stores/props";
import type { TabbedWaiver, TabbedWaiverTab } from "@/types/Waiver";
import type { MenuItem } from "primevue/menuitem";
import { currencyFormatter, getTableData, numberParser, round } from "@/services/Utility";
import CustomDeleteIconCellRenderer from "../cell-renderers/CustomDeleteIconCellRenderer.vue";
import type { GridApi, GridReadyEvent, IsFullWidthRowParams, ValueFormatterParams, ValueParserParams } from "ag-grid-community";

const activeTab = ref(0);

const tabbedWaiversTabs = defineModel<TabbedWaiverTab[]>("tabbedWaiverTabs", { required: true });

interface Props {
  projectId: string;
  canEdit: boolean;
}
const props = defineProps<Props>();

const getTabs = (): void => {
  tabs.value = [];
  tabbedWaiversTabs.value.forEach((tab: TabbedWaiverTab) => {
    const item = { label: tab.description, icon: "", visible: true, key: tab.ordinal.toString() };
    tabs.value.push(item);
  });
};

const tabsRearranged = (): void => {
  for (let i = 0; i < tabs.value.length; i++) {
    const tab = tabs.value[i];
    const tabbedWaiver = tabbedWaiversTabs.value.find((t) => t.description === tab.label);
    if (tabbedWaiver && tab.key) {
      tabbedWaiver.ordinal = parseInt(tab.key);
    }
  }

  tabbedWaiversTabs.value.sort((a, b) => a.ordinal - b.ordinal);
};

const tabs = ref<MenuItem[]>([]);
getTabs();

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const tabbedWaivers = computed<TabbedWaiver[]>(() => {
  if (tabbedWaiversTabs.value.length == 0) return [];
  else return tabbedWaiversTabs.value[activeTab.value].tabbedWaivers;
});

const resize = (): void => {
  gridApi.value?.autoSizeColumns([]);
};

const defaultColDefs = {
  floatingFilter: false,
  sortable: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  headerClass: "grid-align-center text-center",
  cellDataType: "text",
};

const recalculate = (): void => {
  let previousTotalPaidToDate = 0;
  for (let i = 0; i < tabbedWaivers.value.length; i++) {
    const row = gridApi.value?.getDisplayedRowAtIndex(i);
    if (row == null) return;
    if (i === 0) {
      row.setDataValue("totalPaidToDate", row.data.netAmountBilled);
      previousTotalPaidToDate = row.data.netAmountBilled;
    } else {
      const totalPaidToDate = round(row.data.netAmountBilled + previousTotalPaidToDate, 2);
      row.setDataValue("totalPaidToDate", totalPaidToDate);
      previousTotalPaidToDate = totalPaidToDate;
    }
  }
};

const addTab = (newTabName: string): void => {
  const newTab: TabbedWaiverTab = {
    tabId: null,
    projectId: props.projectId,
    description: newTabName,
    ordinal: tabbedWaiversTabs.value.length,
    tabbedWaivers: [],
  };

  tabbedWaiversTabs.value.push(newTab);
  getTabs();
};

const editTab = (newTabName: string, editTabIndex: number): void => {
  tabbedWaiversTabs.value[editTabIndex].description = newTabName;
  getTabs();
};

const deleteTab = (index: number): void => {
  tabbedWaiversTabs.value.splice(index, 1);
  for (let i = index; i < tabbedWaiversTabs.value.length; i++) {
    tabbedWaiversTabs.value[i].ordinal -= 1;
  }
  getTabs();
  activeTab.value = 0;
};

const addNewTabbedWaiver = (): void => {
  if (tabbedWaiversTabs.value.length == 0) {
    error("Invalid Action", "You need to add a tab first");
    return;
  }
  const newTabbedWaiver: TabbedWaiver = {
    tabId: null,
    waiverId: null,
    contractAmount: 0,
    adjustedContractAmount: 0,
    dateOrPeriodThrough: null,
    netAmountBilled: 0,
    gcpayApp: 0,
    subPayAppNum: null,
    conditional: 0,
    unconditional: 0,
    note: "",
    totalPaidToDate: 0,
  };

  gridApi.value?.applyTransaction({
    add: [newTabbedWaiver],
  });
  tabbedWaiversTabs.value[activeTab.value].tabbedWaivers = getTableData(gridApi.value);

  recalculate();
};

const deleteTabbedWaiver = (tabbedWaiver: TabbedWaiver): void => {
  gridApi.value?.applyTransaction({
    remove: [tabbedWaiver],
  });

  tabbedWaiversTabs.value[activeTab.value].tabbedWaivers = getTableData(gridApi.value);
  success("Tabbed Waiver Deleted", "A waiver was successfully deleted");

  recalculate();
};

const dateChanged = (rowIndex: number, date: Date): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData) rowData.setDataValue("dateOrPeriodThrough", date);
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (waiver: TabbedWaiver) => "Are you sure you want to delete this waiver",
      deleteConfirmHeader: () => "Delete Waiver",
      callback: deleteTabbedWaiver,
      checkDeletable: (waiver: TabbedWaiver) => true,
    },
    width: 50,
    minWidth: 50,
    hide: !props.canEdit,
  },
  {
    field: "contractAmount",
    cellDataType: "number",
    headerName: "Contract Amount",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "adjustedContractAmount",
    cellDataType: "number",
    headerName: "Adjusted Contract Amount",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "dateOrPeriodThrough",
    cellDataType: "date",
    headerName: "Date or Period Through",
    cellRenderer: CustomDateCellRenderer,
    cellRendererParams: {
      setValue: dateChanged,
      disableInput: () => !props.canEdit,
    },
    width: 125,
  },
  {
    field: "netAmountBilled",
    cellDataType: "number",
    headerName: "Net Amount Billed",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "gcpayApp",
    cellDataType: "number",
    headerName: "G/C Pay App",
    valueParser: (params: ValueParserParams) => numberParser(params, 0, true),
    width: 50,
    cellClass: "text-center",
    editable: props.canEdit,
  },
  {
    field: "subPayAppNum",
    headerName: "Sub Pay App/Inv #",
    width: 75,
    cellClass: "text-center",
    editable: props.canEdit,
  },
  {
    field: "conditional",
    cellDataType: "number",
    headerName: "Conditional",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "unconditional",
    cellDataType: "number",
    headerName: "Unconditional",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "note",
    headerName: "Note",
    width: 150,
    editable: props.canEdit,
    valueParser: (params: ValueParserParams) => (params.newValue == null ? "" : params.newValue),
  },
  {
    field: "totalPaidToDate",
    cellDataType: "number",
    headerName: "Total Paid To Date",
    valueFormatter: currencyFormatter,
    cellClass: "bg-surface-500/10",
    width: 100,
  },
  {
    field: "",
    cellDataType: "number",
    headerName: "Balance To Finish",
    valueFormatter: (params: ValueFormatterParams) => {
      const amount = round(params.data.adjustedContractAmount - params.data.totalPaidToDate, 2);
      let currency = amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
      return currency.startsWith("-") ? currency.replace("-", "(") + ")" : currency;
    },
    cellClass: "bg-surface-500/10",
    width: 100,
  },
]);
</script>
